import { DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { styled } from '@mui/material/styles';
import translate from '@translate';
import { FC, ReactNode, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Select, { SelectValueType } from 'src/components/Select';
import TextField from 'src/components/TextField';
import Typography from 'src/components/Typography';
import Button from '../../components/Button';
import Checkbox from '../../components/Checkbox';
import Tooltip from '../../components/Tooltip';
import { InfoIcon } from '../../components/SkladonIcon/Icons';
import { useCheckPermission } from '../../utils/checkPermission';
import colorOptionFce from 'src/utils/colorOptionFce';
import { countryOptionsCodes } from 'src/utils/countryOptions';
import { ConstantsCodeType } from 'src/types/constantsCodes';

const IMEI_DEFAULT_REGEX = '^[0-9]{14,16}$';

type MassEditFieldKey =
    | 'PRODUCT_TYPE'
    | 'EXP_METHOD'
    | 'HS_CODE'
    | 'ROP'
    | 'EXPIRATION_DAYS'
    | 'AGECHECK'
    | 'SCAN_IMEI_SN_TYPE'
    | 'ACTIVE'
    | 'IS_DELETED'
    | 'PRICE'
    | 'UNIT_PRICE'
    | 'BATTERY_TYPE'
    | 'ORIGIN_COUNTRY'
    | 'CUSTOM_FIELD_1'
    | 'CUSTOM_FIELD_2'
    | 'CUSTOM_FIELD_3'
    | 'SN_REGEX'
    | 'QUANTITY_CONVERSION';

type MassEditCheckboxKey = Exclude<MassEditFieldKey, 'SN_REGEX'>;

type MassEditFieldValue = string | number;
type MassEditData = Record<MassEditFieldKey, MassEditFieldValue>;
type MassEditCheckedState = Record<MassEditCheckboxKey, boolean>;
type MassEditErrors = Record<MassEditFieldKey, boolean>;

const INITIAL_DATA: MassEditData = {
    PRODUCT_TYPE: '',
    EXP_METHOD: '',
    HS_CODE: '',
    ROP: '',
    EXPIRATION_DAYS: '',
    AGECHECK: '',
    SCAN_IMEI_SN_TYPE: '',
    ACTIVE: '',
    IS_DELETED: '',
    PRICE: '',
    UNIT_PRICE: '',
    BATTERY_TYPE: '',
    ORIGIN_COUNTRY: '',
    CUSTOM_FIELD_1: '',
    CUSTOM_FIELD_2: '',
    CUSTOM_FIELD_3: '',
    SN_REGEX: '',
    QUANTITY_CONVERSION: ''
};

const INITIAL_CHECKED: MassEditCheckedState = {
    PRODUCT_TYPE: false,
    EXP_METHOD: false,
    HS_CODE: false,
    ROP: false,
    EXPIRATION_DAYS: false,
    AGECHECK: false,
    SCAN_IMEI_SN_TYPE: false,
    ACTIVE: false,
    IS_DELETED: false,
    PRICE: false,
    UNIT_PRICE: false,
    BATTERY_TYPE: false,
    ORIGIN_COUNTRY: false,
    CUSTOM_FIELD_1: false,
    CUSTOM_FIELD_2: false,
    CUSTOM_FIELD_3: false,
    QUANTITY_CONVERSION: false
};

const INITIAL_ERRORS: MassEditErrors = {
    PRODUCT_TYPE: false,
    EXP_METHOD: false,
    HS_CODE: false,
    ROP: false,
    EXPIRATION_DAYS: false,
    AGECHECK: false,
    SCAN_IMEI_SN_TYPE: false,
    ACTIVE: false,
    IS_DELETED: false,
    PRICE: false,
    UNIT_PRICE: false,
    BATTERY_TYPE: false,
    ORIGIN_COUNTRY: false,
    CUSTOM_FIELD_1: false,
    CUSTOM_FIELD_2: false,
    CUSTOM_FIELD_3: false,
    SN_REGEX: false,
    QUANTITY_CONVERSION: false
};

const SELECT_FIELDS: MassEditCheckboxKey[] = [
    'PRODUCT_TYPE',
    'EXP_METHOD',
    'AGECHECK',
    'SCAN_IMEI_SN_TYPE',
    'ACTIVE',
    'IS_DELETED',
    'BATTERY_TYPE',
    'UNIT_PRICE',
    'ORIGIN_COUNTRY',
    'QUANTITY_CONVERSION'
];

const isEmptyValue = (value: MassEditFieldValue): boolean => value === '' || value === null || value === undefined;

const normalizeSelectValue = (value: SelectValueType | SelectValueType[]): MassEditFieldValue => {
    if (Array.isArray(value)) {
        return '';
    }
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }
    return value;
};

const InfoIconStyled = styled('span')({
    marginLeft: '10px',
    '& svg': {
        width: '14px',
        height: '14px',
        marginRight: '10px'
    }
});

const FieldsGrid = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column'
    }
}));

const Column = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    [theme.breakpoints.down('sm')]: {
        display: 'contents'
    }
}));

const FieldRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0px'
});

const InputWrapper = styled('div')({
    flex: 1,
    minWidth: 0
});

const CheckboxSpacer = styled('div')({
    width: 42,
    flexShrink: 0
});

export type MassEditDialogPropsType = {
    optionsValue?: SelectValueType;
    options?: ConstantsCodeType[];
    productTypeCodes: ConstantsCodeType[];
    expeditionMethodCodes: ConstantsCodeType[];
    boolNumberCodes: ConstantsCodeType[];
    scanImeiSnCodes: ConstantsCodeType[];
    currencyCodes: ConstantsCodeType[];
    batteryTypeCodes: ConstantsCodeType[];
    content?: string;
    title: string;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    action?: (data: Partial<MassEditData>) => void;
    onClose: () => void;
    onCancel?: () => void;
    disabled?: boolean;
};

const MassEditDialog: FC<MassEditDialogPropsType> = (props) => {
    const [optionsValue, setOptionsValue] = useState<SelectValueType>(props.optionsValue ?? null);
    const [data, setData] = useState<MassEditData>(INITIAL_DATA);
    const [checkedState, setCheckedState] = useState<MassEditCheckedState>(INITIAL_CHECKED);
    const [errors, setErrors] = useState<MassEditErrors>(INITIAL_ERRORS);

    const handleCheckboxChange = (field: MassEditCheckboxKey, value: boolean) => {
        setCheckedState((prevState) => ({
            ...prevState,
            [field]: value
        }));
        if (!value) {
            setData((prevState) => ({
                ...prevState,
                [field]: '',
                ...(field === 'SCAN_IMEI_SN_TYPE' ? { SN_REGEX: '' } : {})
            }));
            setErrors((prevState) => ({
                ...prevState,
                [field]: false,
                ...(field === 'SCAN_IMEI_SN_TYPE' ? { SN_REGEX: false } : {})
            }));
        }
    };

    const handleInputChange = (field: MassEditFieldKey, value: MassEditFieldValue) => {
        if (field === 'SCAN_IMEI_SN_TYPE') {
            const code = value === '' ? '' : String(value);
            const isImei = code.startsWith('imei');
            setData((prevState) => ({
                ...prevState,
                SCAN_IMEI_SN_TYPE: code,
                SN_REGEX: isImei ? IMEI_DEFAULT_REGEX : ''
            }));
            setErrors((prevState) => ({
                ...prevState,
                SCAN_IMEI_SN_TYPE: isEmptyValue(code),
                SN_REGEX: false
            }));
            return;
        }

        setData((prevState) => ({
            ...prevState,
            [field]: value
        }));
        setErrors((prevState) => ({
            ...prevState,
            [field]: isEmptyValue(value)
        }));
    };

    const isAnyCheckboxChecked = () => {
        return Object.values(checkedState).some((isChecked) => isChecked);
    };

    const content = (
        <>
            <Typography component="div">{props.content}</Typography>
            {props.options && (
                <Select
                    options={props.options}
                    value={optionsValue}
                    onChange={(value) => setOptionsValue(Array.isArray(value) ? null : value)}
                    fullWidth
                    required
                />
            )}
        </>
    );

    const handleOk = () => {
        const newErrors = { ...errors };
        let hasError = false;

        SELECT_FIELDS.forEach((key) => {
            if (checkedState[key] && isEmptyValue(data[key])) {
                newErrors[key] = true;
                hasError = true;
            } else {
                newErrors[key] = false;
            }
        });

        setErrors(newErrors);

        if (hasError) {
            return;
        }

        if (props.action) {
            const activeData = (Object.keys(checkedState) as MassEditCheckboxKey[]).reduce<Partial<MassEditData>>(
                (acc, key) => {
                    if (checkedState[key]) {
                        acc[key] = data[key];
                    }
                    return acc;
                },
                {}
            );

            if (checkedState.SCAN_IMEI_SN_TYPE && data.SCAN_IMEI_SN_TYPE !== 'none' && data.SCAN_IMEI_SN_TYPE !== '') {
                activeData.SN_REGEX = data.SN_REGEX;
            }

            props.action(activeData);
        }
        props.onClose();
    };

    const handleCancel = () => {
        if (props.onCancel) {
            props.onCancel();
        }
        props.onClose();
    };

    const permissionExpMethodEdit = useCheckPermission('PRODUCT.EXP_METHOD.EDIT');
    const permissionQuantityConversion = useCheckPermission('PRODUCT.QUANTITY_CONVERSION');

    const renderFieldRow = (checkbox: ReactNode, input: ReactNode) => (
        <FieldRow>
            {checkbox}
            <InputWrapper>{input}</InputWrapper>
        </FieldRow>
    );

    const scanType = String(data.SCAN_IMEI_SN_TYPE || '');
    const showSnRegex = checkedState.SCAN_IMEI_SN_TYPE && scanType !== '' && scanType !== 'none';
    const isImeiSelected = scanType.startsWith('imei');

    return (
        <Dialog maxWidth={props.maxWidth || 'lg'} open fullWidth>
            <DialogTitle id="confirmation-dialog-title">{props.title}</DialogTitle>
            <DialogContent dividers>
                {content}
            </DialogContent>
            <DialogContent dividers>
                <FieldsGrid>
                    <Column>
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.ACTIVE}
                                onChange={(value: boolean | number) => handleCheckboxChange('ACTIVE', !!value)}
                            />,
                            <Select
                                options={props.boolNumberCodes}
                                onChange={(value) => handleInputChange('ACTIVE', normalizeSelectValue(value))}
                                name="ACTIVE"
                                label={translate('productDetail.active')}
                                fullWidth
                                disabled={!checkedState.ACTIVE}
                                value={data.ACTIVE}
                                error={errors.ACTIVE}
                                required={checkedState.ACTIVE}
                                optionStyleFce={colorOptionFce}
                            />
                        )}
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.IS_DELETED}
                                onChange={(value: boolean | number) => handleCheckboxChange('IS_DELETED', !!value)}
                            />,
                            <Select
                                options={props.boolNumberCodes}
                                onChange={(value) => handleInputChange('IS_DELETED', normalizeSelectValue(value))}
                                name="IS_DELETED"
                                label={translate('productDetail.isDeleted')}
                                fullWidth
                                disabled={!checkedState.IS_DELETED}
                                value={data.IS_DELETED}
                                error={errors.IS_DELETED}
                                required={checkedState.IS_DELETED}
                                optionStyleFce={colorOptionFce}
                            />
                        )}
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.PRODUCT_TYPE}
                                onChange={(value: boolean | number) => handleCheckboxChange('PRODUCT_TYPE', !!value)}
                            />,
                            <Select
                                options={props.productTypeCodes}
                                onChange={(value) => handleInputChange('PRODUCT_TYPE', normalizeSelectValue(value))}
                                name="PRODUCT_TYPE"
                                label={translate('productDetail.productType')}
                                fullWidth
                                disabled={!checkedState.PRODUCT_TYPE}
                                value={data.PRODUCT_TYPE}
                                error={errors.PRODUCT_TYPE}
                                required={checkedState.PRODUCT_TYPE}
                            />
                        )}
                        {permissionExpMethodEdit &&
                            renderFieldRow(
                                <Checkbox
                                    value={checkedState.EXP_METHOD}
                                    onChange={(value: boolean | number) => handleCheckboxChange('EXP_METHOD', !!value)}
                                />,
                                <Select
                                    options={props.expeditionMethodCodes}
                                    onChange={(value) => handleInputChange('EXP_METHOD', normalizeSelectValue(value))}
                                    name="EXP_METHOD"
                                    label={translate('productDetail.expMethod')}
                                    fullWidth
                                    disabled={!checkedState.EXP_METHOD}
                                    value={data.EXP_METHOD}
                                    error={errors.EXP_METHOD}
                                    required={checkedState.EXP_METHOD}
                                />
                            )}
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.HS_CODE}
                                onChange={(value: boolean | number) => handleCheckboxChange('HS_CODE', !!value)}
                            />,
                            <TextField
                                onChange={(value) => handleInputChange('HS_CODE', value ?? '')}
                                name="HS_CODE"
                                label={translate('productDetail.hsCode')}
                                fullWidth
                                disabled={!checkedState.HS_CODE}
                                value={data.HS_CODE}
                                error={errors.HS_CODE}
                            />
                        )}
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.BATTERY_TYPE}
                                onChange={(value: boolean | number) => handleCheckboxChange('BATTERY_TYPE', !!value)}
                            />,
                            <Select
                                options={props.batteryTypeCodes}
                                onChange={(value) => handleInputChange('BATTERY_TYPE', normalizeSelectValue(value))}
                                name="BATTERY_TYPE"
                                label={translate('productDetail.batteryType')}
                                fullWidth
                                disabled={!checkedState.BATTERY_TYPE}
                                value={data.BATTERY_TYPE}
                                error={errors.BATTERY_TYPE}
                                required={checkedState.BATTERY_TYPE}
                                optionStyleFce={colorOptionFce}
                            />
                        )}
                    </Column>

                    <Column>
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.AGECHECK}
                                onChange={(value: boolean | number) => handleCheckboxChange('AGECHECK', !!value)}
                            />,
                            <Select
                                options={props.boolNumberCodes}
                                onChange={(value) => handleInputChange('AGECHECK', normalizeSelectValue(value))}
                                name="AGECHECK"
                                label={translate('productDetail.agecheck')}
                                fullWidth
                                disabled={!checkedState.AGECHECK}
                                value={data.AGECHECK}
                                error={errors.AGECHECK}
                                required={checkedState.AGECHECK}
                                optionStyleFce={colorOptionFce}
                            />
                        )}
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.ROP}
                                onChange={(value: boolean | number) => handleCheckboxChange('ROP', !!value)}
                            />,
                            <TextField
                                onChange={(value) => handleInputChange('ROP', value ?? '')}
                                name="ROP"
                                label={translate('productDetail.ROP')}
                                fullWidth
                                type="numberLocale"
                                disabled={!checkedState.ROP}
                                value={data.ROP}
                                error={errors.ROP}
                            />
                        )}
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.EXPIRATION_DAYS}
                                onChange={(value: boolean | number) =>
                                    handleCheckboxChange('EXPIRATION_DAYS', !!value)
                                }
                            />,
                            <TextField
                                onChange={(value) => handleInputChange('EXPIRATION_DAYS', value ?? '')}
                                name="EXPIRATION_DAYS"
                                label={translate('productDetail.EXPIRATION_DAYS')}
                                fullWidth
                                type="numberLocale"
                                disabled={!checkedState.EXPIRATION_DAYS}
                                value={data.EXPIRATION_DAYS}
                                error={errors.EXPIRATION_DAYS}
                            />
                        )}
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.ORIGIN_COUNTRY}
                                onChange={(value: boolean | number) => handleCheckboxChange('ORIGIN_COUNTRY', !!value)}
                            />,
                            <Select
                                options={countryOptionsCodes}
                                onChange={(value) => handleInputChange('ORIGIN_COUNTRY', normalizeSelectValue(value))}
                                name="ORIGIN_COUNTRY"
                                label={translate('productDetail.originCountry')}
                                fullWidth
                                disabled={!checkedState.ORIGIN_COUNTRY}
                                value={data.ORIGIN_COUNTRY}
                                error={errors.ORIGIN_COUNTRY}
                                required={checkedState.ORIGIN_COUNTRY}
                            />
                        )}
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.SCAN_IMEI_SN_TYPE}
                                onChange={(value: boolean | number) =>
                                    handleCheckboxChange('SCAN_IMEI_SN_TYPE', !!value)
                                }
                            />,
                            <Select
                                options={props.scanImeiSnCodes}
                                onChange={(value) =>
                                    handleInputChange('SCAN_IMEI_SN_TYPE', normalizeSelectValue(value))
                                }
                                name="SCAN_IMEI_SN_TYPE"
                                label={translate('productDetail.scanSerialNumber')}
                                fullWidth
                                disabled={!checkedState.SCAN_IMEI_SN_TYPE}
                                value={data.SCAN_IMEI_SN_TYPE}
                                error={errors.SCAN_IMEI_SN_TYPE}
                                required={checkedState.SCAN_IMEI_SN_TYPE}
                                optionStyleFce={colorOptionFce}
                            />
                        )}
                        {showSnRegex && (
                            <FieldRow>
                                <CheckboxSpacer />
                                <InputWrapper>
                                    <TextField
                                        onChange={(value) => handleInputChange('SN_REGEX', value ?? '')}
                                        name="SN_REGEX"
                                        label={
                                            <>
                                                {translate('productDetail.snRegex')}
                                                <Tooltip title={translate('productDetail.snRegex.tooltip')}>
                                                    <InfoIconStyled>
                                                        <InfoIcon />
                                                    </InfoIconStyled>
                                                </Tooltip>
                                            </>
                                        }
                                        fullWidth
                                        disabled={isImeiSelected}
                                        value={data.SN_REGEX}
                                        error={errors.SN_REGEX}
                                        multiline
                                    />
                                </InputWrapper>
                            </FieldRow>
                        )}
                    </Column>

                    <Column>
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.PRICE}
                                onChange={(value: boolean | number) => handleCheckboxChange('PRICE', !!value)}
                            />,
                            <TextField
                                onChange={(value) => handleInputChange('PRICE', value ?? '')}
                                name="PRICE"
                                label={translate('productDetail.price')}
                                fullWidth
                                type="numberLocale"
                                min={0}
                                disabled={!checkedState.PRICE}
                                value={data.PRICE}
                                error={errors.PRICE}
                            />
                        )}
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.UNIT_PRICE}
                                onChange={(value: boolean | number) => handleCheckboxChange('UNIT_PRICE', !!value)}
                            />,
                            <Select
                                options={props.currencyCodes}
                                onChange={(value) => handleInputChange('UNIT_PRICE', normalizeSelectValue(value))}
                                name="UNIT_PRICE"
                                label={translate('productDetail.unitPrice')}
                                fullWidth
                                disabled={!checkedState.UNIT_PRICE}
                                value={data.UNIT_PRICE}
                                error={errors.UNIT_PRICE}
                                required={checkedState.UNIT_PRICE}
                            />
                        )}
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.CUSTOM_FIELD_1}
                                onChange={(value: boolean | number) => handleCheckboxChange('CUSTOM_FIELD_1', !!value)}
                            />,
                            <TextField
                                onChange={(value) => handleInputChange('CUSTOM_FIELD_1', value ?? '')}
                                name="CUSTOM_FIELD_1"
                                label={translate('productDetail.customField1')}
                                fullWidth
                                disabled={!checkedState.CUSTOM_FIELD_1}
                                value={data.CUSTOM_FIELD_1}
                                error={errors.CUSTOM_FIELD_1}
                            />
                        )}
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.CUSTOM_FIELD_2}
                                onChange={(value: boolean | number) => handleCheckboxChange('CUSTOM_FIELD_2', !!value)}
                            />,
                            <TextField
                                onChange={(value) => handleInputChange('CUSTOM_FIELD_2', value ?? '')}
                                name="CUSTOM_FIELD_2"
                                label={translate('productDetail.customField2')}
                                fullWidth
                                disabled={!checkedState.CUSTOM_FIELD_2}
                                value={data.CUSTOM_FIELD_2}
                                error={errors.CUSTOM_FIELD_2}
                            />
                        )}
                        {renderFieldRow(
                            <Checkbox
                                value={checkedState.CUSTOM_FIELD_3}
                                onChange={(value: boolean | number) => handleCheckboxChange('CUSTOM_FIELD_3', !!value)}
                            />,
                            <TextField
                                onChange={(value) => handleInputChange('CUSTOM_FIELD_3', value ?? '')}
                                name="CUSTOM_FIELD_3"
                                label={translate('productDetail.customField3')}
                                fullWidth
                                disabled={!checkedState.CUSTOM_FIELD_3}
                                value={data.CUSTOM_FIELD_3}
                                error={errors.CUSTOM_FIELD_3}
                            />
                        )}
                        {permissionQuantityConversion &&
                            renderFieldRow(
                                <Checkbox
                                    value={checkedState.QUANTITY_CONVERSION}
                                    onChange={(value: boolean | number) =>
                                        handleCheckboxChange('QUANTITY_CONVERSION', !!value)
                                    }
                                />,
                                <Select
                                    options={props.boolNumberCodes}
                                    onChange={(value) =>
                                        handleInputChange('QUANTITY_CONVERSION', normalizeSelectValue(value))
                                    }
                                    name="QUANTITY_CONVERSION"
                                    label={translate('productDetail.quantityConversion')}
                                    fullWidth
                                    disabled={!checkedState.QUANTITY_CONVERSION}
                                    value={data.QUANTITY_CONVERSION}
                                    error={errors.QUANTITY_CONVERSION}
                                    required={checkedState.QUANTITY_CONVERSION}
                                    optionStyleFce={colorOptionFce}
                                />
                            )}
                    </Column>
                </FieldsGrid>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={handleCancel}>
                    {translate('confirmDialog.cancel')}
                </Button>
                {props.action && (
                    <Button variant="contained" onClick={handleOk} disabled={props.disabled || !isAnyCheckboxChecked()}>
                        {translate('massEditProductDialog.ok')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default MassEditDialog;
